import React, { useContext, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { AlertTriangle, Check, Sprout } from "lucide-react";
import AuthContext from "../../../contexts/AuthContext/AuthContext";
import {
  providerSignIn,
  providerSignUp,
  requestPasswordReset,
} from "../../../services/CareTeamService";
import PasswordInput from "../../common/PasswordInput";
import { PROFESSIONS, apiErrorMessage } from "../careUtils";
import { Plate, icon } from "../ui";
import { MEADOW_PATH } from "../art";
import "../careTeam.css";

const FormError = ({ text }) =>
  text ? (
    <p className="ct-form-error" role="alert">
      {icon(AlertTriangle)}
      <span>{text}</span>
    </p>
  ) : null;

// The portal's own reset form, so providers stay in the portal instead of
// landing on the players' sign-in page.
const ForgotPasswordForm = ({ onBack }) => {
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await requestPasswordReset(email.trim());
      setSentTo(email.trim());
    } catch (err) {
      // Djoser can be set to say an address isn't registered; don't reveal it.
      if (err?.response?.status === 404 || err?.response?.status === 400) {
        setSentTo(email.trim());
      } else {
        setError(apiErrorMessage(err, "We couldn't send the email. Please try again."));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="ct-auth__form" onSubmit={submit}>
      <div className="ct-field" style={{ gap: 6 }}>
        <span className="ct-kicker">Provider portal</span>
        <h1>Reset your password</h1>
      </div>
      {sentTo ? (
        <p className="ct-success" role="status" style={{ alignItems: "flex-start" }}>
          {icon(Check)}
          <span>
            If a provider account uses {sentTo}, we've sent it a link to choose a new
            password. If it's not in your inbox within a few minutes, look in your junk
            or spam folder. It's from support@mendgrove.com.
          </span>
        </p>
      ) : (
        <>
          <p className="ct-muted" style={{ fontSize: 15 }}>
            Enter your work email and we'll send you a link to choose a new password.
          </p>
          <div className="ct-field">
            <label htmlFor="ct-reset-email">Work email</label>
            <input
              id="ct-reset-email"
              className="ct-input"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <FormError text={error} />
          <button
            type="submit"
            className="ct-btn ct-btn--primary"
            style={{ minHeight: 48 }}
            disabled={busy}
          >
            {busy ? "Sending…" : "Send reset link"}
          </button>
        </>
      )}
      <button type="button" className="ct-link-button" onClick={onBack}>
        Back to sign in
      </button>
    </form>
  );
};

const SignInForm = ({ onForgot }) => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { access, refresh, user } = await providerSignIn(email, password);
      login(user, access, refresh, { remember });
      navigate("/provider/patients");
    } catch (err) {
      setError(apiErrorMessage(err, "We couldn't sign you in. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="ct-auth__form" onSubmit={submit}>
      <div className="ct-field" style={{ gap: 6 }}>
        <span className="ct-kicker">Provider portal</span>
        <h1>Sign in</h1>
      </div>
      <div className="ct-field">
        <label htmlFor="ct-email">Work email</label>
        <input
          id="ct-email"
          className="ct-input"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="ct-field">
        <label htmlFor="ct-password">Password</label>
        <PasswordInput
          id="ct-password"
          className="ct-input"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      <div className="ct-auth__row">
        <label className="ct-check" style={{ alignItems: "center" }}>
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            style={{ marginTop: 0 }}
          />
          Keep me signed in on this device
        </label>
        <button type="button" className="ct-link-button" onClick={onForgot}>
          Forgot password?
        </button>
      </div>
      <FormError text={error} />
      <button
        type="submit"
        className="ct-btn ct-btn--primary"
        style={{ minHeight: 48 }}
        disabled={busy}
      >
        {busy ? "Signing in…" : "Sign in"}
      </button>
      <hr className="ct-hairline" />
      <p style={{ fontSize: 15 }}>
        New to MendGrove for clinicians?{" "}
        <Link to="/provider/signup">Create a provider account</Link>
      </p>
      <p className="ct-muted" style={{ fontSize: 14 }}>
        Looking for the games? <Link to="/signin">Go to the player sign-in</Link>
      </p>
    </form>
  );
};

const SignUpForm = () => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    profession: "",
    organization: "",
    password: "",
    agree: false,
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (key) => (e) =>
    setForm((f) => ({
      ...f,
      [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.profession) {
      setError("Please select your profession.");
      return;
    }
    if (!form.agree) {
      setError("Please agree to the provider terms.");
      return;
    }
    setBusy(true);
    try {
      const { access, refresh, user } = await providerSignUp(form);
      login(user, access, refresh);
      navigate("/provider/patients", {
        state: { toast: "Account created. Welcome to MendGrove." },
      });
    } catch (err) {
      setError(apiErrorMessage(err, "We couldn't create your account. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="ct-auth__form ct-auth__form--signup" onSubmit={submit}>
      <div className="ct-field" style={{ gap: 6 }}>
        <span className="ct-kicker">Provider portal</span>
        <h1>Create a provider account</h1>
        <p className="ct-muted" style={{ fontSize: 15 }}>
          Free for licensed clinicians. Patients connect only after they approve.
        </p>
      </div>
      <div className="ct-field">
        <label htmlFor="ct-name">Full name</label>
        <input
          id="ct-name"
          className="ct-input"
          placeholder="e.g. Dr. Hannah Moss"
          autoComplete="name"
          required
          value={form.full_name}
          onChange={set("full_name")}
        />
      </div>
      <div className="ct-field">
        <label htmlFor="ct-email">Work email</label>
        <input
          id="ct-email"
          className="ct-input"
          type="email"
          placeholder="name@clinic.org"
          autoComplete="email"
          required
          value={form.email}
          onChange={set("email")}
        />
      </div>
      <div className="ct-field">
        <label htmlFor="ct-profession">Profession</label>
        <select
          id="ct-profession"
          className="ct-input"
          value={form.profession}
          onChange={set("profession")}
        >
          <option value="">Select your profession</option>
          {PROFESSIONS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>
      <div className="ct-field">
        <label htmlFor="ct-org">
          Organization <span className="ct-optional">(optional)</span>
        </label>
        <input
          id="ct-org"
          className="ct-input"
          placeholder="Clinic, hospital or practice"
          autoComplete="organization"
          value={form.organization}
          onChange={set("organization")}
        />
      </div>
      <div className="ct-field">
        <label htmlFor="ct-password">Password</label>
        <PasswordInput
          id="ct-password"
          className="ct-input"
          placeholder="At least 12 characters"
          autoComplete="new-password"
          minLength={12}
          required
          value={form.password}
          onChange={set("password")}
        />
      </div>
      <label className="ct-check">
        <input type="checkbox" checked={form.agree} onChange={set("agree")} />
        I agree to the provider terms and will use patient data only for their care.
      </label>
      <FormError text={error} />
      <button
        type="submit"
        className="ct-btn ct-btn--primary"
        style={{ minHeight: 48 }}
        disabled={busy}
      >
        {busy ? "Creating account…" : "Create account"}
      </button>
      <p style={{ fontSize: 15 }}>
        Already have an account? <Link to="/provider/signin">Sign in</Link>
      </p>
    </form>
  );
};

// /provider/signin and /provider/signup
const ProviderAuthPage = ({ mode = "signin" }) => {
  const { isSignedIn, isClinician } = useContext(AuthContext);
  const [forgot, setForgot] = useState(false);
  if (isSignedIn && isClinician) return <Navigate to="/provider/patients" replace />;
  return (
  <div className="ct ct-provider">
    <Helmet>
      <title>
        {mode === "signup" ? "Create a provider account" : "Provider sign in"} – Mend Grove
      </title>
      <meta name="robots" content="noindex, follow" />
    </Helmet>
    <div className="ct-auth">
      <div className="ct-auth__intro">
        <div className="ct-brand">
          {icon(Sprout, 22)}
          <span className="ct-brand__name">MendGrove</span>
          <span className="ct-brand__tag">For clinicians</span>
        </div>
        <Plate
          className="ct-auth__plate"
          label="Illustration: a meadow path at the edge of a wood"
          art={MEADOW_PATH}
        />
        <div className="ct-auth__pitch">
          <h2>Follow your patients' cognitive practice between appointments.</h2>
        </div>
      </div>
      <div className="ct-auth__form-col">
        {mode === "signup" ? (
          <SignUpForm />
        ) : forgot ? (
          <ForgotPasswordForm onBack={() => setForgot(false)} />
        ) : (
          <SignInForm onForgot={() => setForgot(true)} />
        )}
      </div>
    </div>
  </div>
  );
};

export default ProviderAuthPage;
